/**
 * Allows teachers to affirm their formal teacher status by checking off a
 * terms of service (TOS) checkbox.
 */

import {t} from "@lingui/core/macro";
import {Trans} from "@lingui/react/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {Choice, RadioGroup} from "@khanacademy/wonder-blocks-form";
import Link from "@khanacademy/wonder-blocks-link";
import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {Caption, LabelMedium} from "@khanacademy/wonder-blocks-typography";
import {usePublishAnalyticsEvent} from "@repo/analytics-learning-time";

type Props = {
    hasFormalTeacherStatus: boolean;
    onChangeHasFormalTeacherStatus: (hasFormalTeacherStatus: boolean) => void;
    isLoading: boolean;
    isError: boolean;
    isDisabled: boolean;
};

const YES = "Yes";
const NO = "No";

const TeacherTOSAffirmation = (props: Props): React.ReactElement => {
    const {
        hasFormalTeacherStatus,
        onChangeHasFormalTeacherStatus,
        isLoading,
        isError,
        isDisabled,
    } = props;
    const publishAnalyticsEvent = usePublishAnalyticsEvent();

    // TODO(hannah): Handle loading where we fetch the data.
    if (isLoading) {
        return (
            <View style={styles.loadingContainer}>
                <CircularSpinner size="small" />
            </View>
        );
    }

    return (
        <View>
            <RadioGroup
                groupName="formal-teacher-tos"
                label={
                    <LabelMedium style={styles.boldLabel}>
                        {t`Are you a teacher using Khan Academy on behalf of a school?`}
                    </LabelMedium>
                }
                onChange={(selectedValue) => {
                    onChangeHasFormalTeacherStatus(selectedValue === YES);
                    publishAnalyticsEvent({
                        eventType: "SchoolPickerFormalTeacherSelected",
                        eventSchemaVersion: 2,
                        isFormalTeacher: selectedValue === YES,
                    });
                }}
                selectedValue={hasFormalTeacherStatus ? YES : NO}
            >
                <Choice
                    label={
                        <Trans>
                            Yes. I am a teacher using this service on behalf of
                            a school. I will comply with requirements for
                            creating and managing School Accounts, as described
                            in the{" "}
                            <Link
                                href="/about/docs/khan-academy-terms-of-service#school-use"
                                target="_blank"
                                inline
                            >
                                {t`Terms of Service`}
                            </Link>
                            .
                        </Trans>
                    }
                    value={YES}
                    disabled={isDisabled || isError}
                    testId="formal-teacher-tos-Yes"
                />
                <Choice
                    label={
                        <Trans>
                            No. I am requesting access to teaching tools as a{" "}
                            <Link
                                href="/about/docs/khan-academy-terms-of-service#school-use"
                                target="_blank"
                                inline
                            >
                                {t`Coach`}
                            </Link>
                            . I understand that any learner I coach will have to
                            create accounts on their own, with parent approval
                            for younger learners.
                        </Trans>
                    }
                    value={NO}
                    disabled={isDisabled || isError}
                    testId="formal-teacher-tos-No"
                />
            </RadioGroup>
            <div aria-live="polite">
                {isError && (
                    <Caption style={styles.error}>
                        {t`Oops. Something went wrong. Please try again.`}
                    </Caption>
                )}
            </div>
        </View>
    );
};

const styles = StyleSheet.create({
    loadingContainer: {
        flex: 1,
        justifyContent: "center",
        alignItems: "center",
    },
    boldLabel: {
        fontWeight: "bold",
    },
    error: {
        color: color.red,
        marginTop: spacing.xSmall_8,
    },
});

export default TeacherTOSAffirmation;
